import React from "react"

import {
	Box,
	Grid,
	ToggleButtonGroup,
} from "@mui/material"

import {
	plural,
	t,
	Trans,
} from "@lingui/macro"

import {
	ErrorResponse,
	RestResponse,
} from "@sinossi/mates-react-library"

import {
	Action,
	ActionTimeline,
	PaginatedResponse,
	SearchFilter,
	SessionResponse,
} from "models"
import { ActionsServices } from "services/actions.services"
import { PageSectionTitle } from "components/commons/page-section-title"
import { SbToggleButton } from "components/commons"

import { FirstColumn } from "./actions-column/first-column"
import SecondColumn from "./actions-column/second-column"
import { ThirdColumn } from "./actions-column/third-column"
import { History } from "./history/history"


interface ActionsComponentProps {
	urgentData: PaginatedResponse<Action> | null
	programmedData: PaginatedResponse<Action> | null
	sessionData: SessionResponse
	historyData: PaginatedResponse<Action> | null
	selectedAction: Action | null
	handlePaginationFirstColumn: (actionType: string, pageId: number) => void
	handleHistoryPagination: (pageId: number) => void
	onSectionChanged: (kind: string | null) => void
	onException: (response: RestResponse<any, ErrorResponse>) => void
	openDeferAction: (data: Action) => void
	openMarkAsDone: (data: Action) => void
	openAssignAction: (data: Action) => void
	openSkipDialog: (data: Action) => void
	updateAction: (action: Action) => void
	updateSection: (actionType: string) => void
	refresh: () => void
}
interface ActionComponentState {
	selectedCard: Action | null
	activeSection: string | null
	timelineActions: PaginatedResponse<ActionTimeline> | null
	timelineRequest: SearchFilter
}

export class ActionsComponent extends React.Component<ActionsComponentProps, ActionComponentState> {
	constructor(props: ActionsComponentProps) {
		super(props)
		this.state = {
			selectedCard: this.props.selectedAction,
			activeSection: "ACTIONS",
			timelineActions: null,
			timelineRequest: SearchFilter.empty(),
		}
	}

	componentDidUpdate(prevProps: Readonly<ActionsComponentProps>, prevState: Readonly<ActionComponentState>, snapshot?: any) {
		if (prevProps.selectedAction !== this.props.selectedAction) {
			this.setState(() => ({
				selectedCard: this.props.selectedAction,
				timelineActions: null
			}))
		}
	}

	private handleFirstColumnCallback = (type: string, id: number) => {
		if (!this.props.urgentData && !this.props.programmedData) {
			return
		}

		let item: Action | null = null
		if (type === "URGENT" && this.props.urgentData) {
			item = this.props.urgentData.items[id]
		}
		else if (type === "PROGRAMMED" && this.props.programmedData) {
			item = this.props.programmedData.items[id]
		}
		else {
			return
		}
		if (!item) {
			return
		}

		this.setState(
			() => ({
				selectedCard: item,
				timelineRequest: SearchFilter.empty(),
				timelineActions: null,
			}), () => {
				this.loadTimeline()
				this.props.updateSection(type)
			}

		)
	}

	private loadTimeline = (): void => {
		ActionsServices.getActionTimeline(
			this.state.selectedCard!.id,
			this.state.timelineRequest,
			this.timelineCallback,
			this.props.onException
		)
	}

	private timelineCallback = (response: RestResponse<PaginatedResponse<ActionTimeline>, ErrorResponse>): void => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({ timelineActions: response.data }))
	}

	private changeSection = (_event: React.MouseEvent<HTMLElement>, section: string | null) => {
		this.setState(
			() => ({ activeSection: section, timelineActions: null, selectedCard: null }),
			() => this.props.onSectionChanged(section)
		)
	}

	private handleHistoryPagination = (pageId: number) => {
		this.props.handleHistoryPagination(pageId)
	}

	private onTimelinePageChange = (pageId: number): void => {
		const { timelineRequest } = this.state
		timelineRequest.pageId = pageId

		this.setState(
			() => ({ timelineRequest: timelineRequest }),
			this.loadTimeline
		)
	}

	private updateContent = (newContent: Action) => {
		this.props.updateAction(newContent)
		this.loadTimeline()
	}

	render() {
		let title: string
		if (this.state.activeSection === "ACTIONS") {
			title = plural(
				this.props.urgentData?.pagination.totalRecords ?? 0,
				{
					one: "# Azione da fare",
					other: "# Azioni da fare",
				}
			)
		} else {
			title = t({ id: "actions.header.history", message: "Storico Azioni" })
		}

		return (
			<Box sx={{ mt: 3, bgcolor: "background.default" }}>
				<PageSectionTitle title={title}>
					<ToggleButtonGroup
						value={this.state.activeSection}
						exclusive
						onChange={this.changeSection}
						aria-label="text alignment"
					>
						<SbToggleButton value="ACTIONS">
							<Trans id="actions.actions">
								Azioni
							</Trans>
						</SbToggleButton>
						<SbToggleButton value="HISTORY">
							<Trans id="actions.history">
								Storico
							</Trans>
						</SbToggleButton>
					</ToggleButtonGroup>
				</PageSectionTitle>

				{this.state.activeSection === "ACTIONS" &&
					<Grid container alignItems={"start"} columnSpacing={3} maxHeight={"70vh"}>
						<Grid item sm={12} md={5}>
							<FirstColumn
								urgent={this.props.urgentData}
								programmed={this.props.programmedData}
								sessionData={this.props.sessionData}
								handleCallback={this.handleFirstColumnCallback}
								handlePaginationCallback={this.props.handlePaginationFirstColumn}
								openDeferDialog={this.props.openDeferAction}
								openMarkAsDone={this.props.openMarkAsDone}
								openAssignDialog={this.props.openAssignAction}
								openSkipDialog={this.props.openSkipDialog}
							/>
						</Grid>
						<Grid item sm={12} md={4}>
							{
								this.state.selectedCard &&
								<SecondColumn action={this.state.selectedCard}
									refresh={this.updateContent}
									openDeferDialog={this.props.openDeferAction}
									openMarkAsDone={this.props.openMarkAsDone}
									openAssignDialog={this.props.openAssignAction}
									openSkipDialog={this.props.openSkipDialog}
								/>
							}
						</Grid>
						<Grid item sm={12} md={3}>
							{
								this.state.timelineActions !== null &&
								<ThirdColumn
									handlePagination={this.onTimelinePageChange}
									data={this.state.timelineActions} />
							}
						</Grid>
					</Grid>
				}
				{this.state.activeSection === "HISTORY" &&
					<Grid container height={"65vh"}>
						<Grid item xs={12}>
							<History data={this.props.historyData} handlePaginationCallback={this.handleHistoryPagination} />
						</Grid>
					</Grid>
				}
			</Box>
		)
	}
}
